import type React from "react";
import { useEffect, useRef, useState } from "react";

/**
 * A popover that previews on hover or focus and pins on click: hovering opens it after
 * `openDelay`, leaving closes it after a short grace (so the pointer can cross into it) unless it
 * was pinned, and clicking the trigger toggles the pin. Spread `contentProps` on the content.
 */
export function useHoverPin(openDelay: number) {
  const [open, setOpen] = useState(false);
  const pinned = useRef(false);
  const openTimer = useRef<ReturnType<typeof setTimeout>>();
  const closeTimer = useRef<ReturnType<typeof setTimeout>>();
  const keepOpen = () => clearTimeout(closeTimer.current);
  const cancel = () => {
    clearTimeout(openTimer.current);
    keepOpen();
  };
  useEffect(() => cancel, []);
  const show = (delayed = false) => {
    cancel();
    if (delayed && !open) openTimer.current = setTimeout(() => setOpen(true), openDelay);
    else setOpen(true);
  };
  const leave = () => {
    cancel();
    closeTimer.current = setTimeout(() => {
      if (!pinned.current) setOpen(false);
    }, 150);
  };
  const togglePin = () => {
    cancel();
    pinned.current = !pinned.current;
    setOpen(pinned.current);
  };
  const onOpenChange = (value: boolean) => {
    cancel();
    pinned.current = value;
    setOpen(value);
  };
  const contentProps = {
    onPointerEnter: keepOpen,
    onPointerLeave: leave,
    onFocusCapture: keepOpen,
    onBlurCapture: (event: React.FocusEvent<HTMLElement>) => {
      if (!event.currentTarget.contains(event.relatedTarget)) leave();
    },
    onOpenAutoFocus: (event: Event) => event.preventDefault(),
    onCloseAutoFocus: (event: Event) => event.preventDefault(),
  };
  return { open, onOpenChange, show, leave, keepOpen, togglePin, contentProps };
}
